import angular from 'angular';
import {guard} from '@acng/frontend-rubicon';
import {popup} from '@acng/frontend-discovery';
import {useLocale} from 'acng/locale/model/locale.js';
import {authUser, hasFeature} from 'acng/core/service/env';
import {portal} from '../../core/portal.js';
import {provideAuthUser} from '../context/auth-user.js';
import {listen} from 'acng/core/context/event-bus.js';
import {AUTH_USER_DATA, EVENTBUS_USER} from '../model/user.js';
import {fsk} from 'acng/userPool/fsk.js';
import {max} from '@acng/frontend-bounty/std/number.js';

const ERR_LOGOUT_GUEST = 'A guest cannot logout.';

angular
  .module('userPool')
  .factory('user', [
    '$rootScope',
    'userPoolApi',
    '$translate',
    '$location',
    'onsOverlay',
    'http',
    'freeCoinsConfig',
    'payment',
    /**
     * @param {import('acng/zz-app').RootScope} $rootScope
     * @param {unknown} userPoolApi
     * @param {angular.translate.ITranslateService} $translate
     * @param {angular.ILocationService} $location
     * @param {import('acng/core/service/overlay').OverlayConstructor} onsOverlay
     * @param {core.Http} http
     * @param {unknown} freeCoinsConfig
     * @param {import('acng/payment/factory/payment').Payment} payment
     */
    function (
      $rootScope,
      userPoolApi,
      $translate,
      $location,
      onsOverlay,
      http,
      freeCoinsConfig,
      payment,
    ) {
      /** @implements {Partial<AuthUserData>} */
      class User {
        nickname = 'guest';
        guest = true;
        fsk = portal.guest_fsk;
        niche = portal.niche;
        list_sorting_suffix = portal.list_sorting_suffix;
        is_international = portal.is_international;
        redirectPath = null;

        get name() {
          return this.nickname;
        }

        get lang() {
          return document.body.lang;
        }

        get underage() {
          return this.fsk < 18;
        }

        get international() {
          return this.is_international;
        }

        get domestic() {
          return !this.international;
        }

        get adult() {
          return !this.underage;
        }
      }

      User.prototype.hasGames = function () {
        return !this.guest && hasFeature('games');
      };

      User.prototype.hasVoiceMessage = function () {
        return !this.guest && hasFeature('messenger');
      };

      User.prototype.guestSignup = function (reason, params) {
        if (this.guest) {
          if (this.preview) {
            return true;
          }
          if (onsOverlay.count()) {
            console.warn('Overlay already open', onsOverlay.count());
            return true;
          }
          if (typeof reason == 'object') {
            params = reason.params;
            reason = reason.reason;
          }
          onsOverlay
            .create('signup', {reason: reason, reasonParams: angular.toJson(params)})
            .disableBackdrop()
            .open();
          return true;
        }
        return false;
      };

      User.prototype.fulfillsFskRequirement = function (fsk, reason, params) {
        if (this.fsk >= fsk) {
          return true;
        }

        payment.overlay(reason, params);
        return false;
      };

      User.prototype.goto = function (path) {
        if (!this.guestSignup()) {
          $location.url(path);
        }
      };

      if (new URL(location.hash.substring(1), location.href).searchParams.has('logout')) {
        history.replaceState(null, '', '#/');
        $translate('userPool.logoutSuccess').then((text) => popup().info(text));
      }

      User.prototype.clear = function () {
        if (this.guest) {
          throw Error(ERR_LOGOUT_GUEST);
        }

        history.pushState(null, '', '#/?logout');
        location.reload();
      };

      User.prototype.getFirstLogin = function () {
        return new Date(this.first_login);
      };

      User.prototype.checkFirstLoginWithinDays = function (days) {
        return this.getFirstLogin().getTime() >= Date.now() - days * 24 * 60 * 60 * 1000;
      };

      User.prototype.getDays = function () {
        const days = Math.ceil((Date.now() - this.getFirstLogin().getTime()) / (24 * 60 * 60 * 1000));
        return days;
      };

      User.prototype.isPremium = function () {
        return this.level >= 10;
      };

      User.prototype.rename = function (nickname) {
        return userPoolApi.rename(nickname).then(function (res) {
          angular.extend($rootScope.user, angular.fromJson(res.data));
          $translate('userPool.nicknameChanged').then(function (text) {
            popup().info(text);
          });
        });
      };

      User.prototype.changeEmail = function (email) {
        return userPoolApi.changeEmail(email);
      };

      User.prototype.getProfile = function () {
        return userPoolApi.getProfile();
      };

      User.prototype.setDescription = function (text) {
        return http().put('/api/userProfile/description', {text}, {dontIntercept: true});
      };

      User.prototype.isInSplittyGroup = function (splitTestId, splitTestGroup) {
        return (
          (this.split_tests || []).find(
            (element) => element.id == splitTestId && element.group == splitTestGroup
          ) !== void 0
        );
      };

      User.prototype.isInGroup = function (groupName) {
        if (!Array.isArray(this.usergroups)) {
          return false;
        }
        return this.usergroups.find((v) => v.name == groupName);
      };

      User.prototype.usesMetricUnits = function () {
        return !this.isInGroup('uk');
      };

      User.prototype.isInternational = function () {
        return this.is_international;
      };

      User.prototype.getsFreeCoinsForDoi = function () {
        return this.doi_reward_group === 'free_coins';
      };

      User.prototype.getFreeCoinsValue = function () {
        return freeCoinsConfig.value;
      };

      User.prototype.hasNiche = function (niche_tags) {
        return Array.isArray(niche_tags) && niche_tags.includes(this.niche);
      };

      /**
       * @returns {18 | 16 | 12}
       */
      User.prototype.getListFsk = function () {
        // @ts-ignore
        return max(portal.list_base_fsk, this.fsk);
      };

      /**
       * @type {User}
       */
      var user;

      if (authUser) {
        Object.setPrototypeOf(authUser, User.prototype);
        user = /** @type {User} */ (authUser);
        user.guest = false;
      } else {
        user = new User();
      }

      listen('user', (data) => {
        ASSERT: guard(data, EVENTBUS_USER);

        $rootScope.$applyAsync(function () {
          const flush = user.fsk != data.user.fsk || user.level != data.user.level;

          user.coins = data.user.coins;
          user.level = data.user.level;
          user.fsk = data.user.fsk;
          fsk.set(user.fsk);

          if (flush) {
            provideAuthUser(user);
          }
        });
      });

      $rootScope.$on('userPool.login.success', function (ev, data) {
        const authUser = angular.fromJson(data);
        ASSERT: guard(authUser, AUTH_USER_DATA);
        user.guest = false;
        if (authUser.id != user.id) {
          Object.assign(user, authUser);
          provideAuthUser(user);
          useLocale(data.preferred_language);
          fsk.set(user.fsk);
        }
      });

      $rootScope.$on('userPool.redirect.login', function (ev, path) {
        // TODO check this
        user.redirectPath = path;
      });

      return user;
    },
  ])

  .factory('User', ['user', (user) => user]);
